import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/cn";

interface PanelProps extends HTMLAttributes<HTMLElement> {
  overflow?: "hidden" | "visible";
}

export function Panel({ className, overflow = "hidden", ...props }: PanelProps) {
  return (
    <section
      className={cn(
        overflow === "visible" ? "overflow-visible" : "overflow-hidden",
        "rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-surface)]",
        className,
      )}
      {...props}
    />
  );
}

interface PanelHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title?: ReactNode;
  meta?: ReactNode;
  action?: ReactNode;
}

export function PanelHeader({
  className,
  children,
  title,
  meta,
  action,
  ...props
}: PanelHeaderProps) {
  if (title || meta || action) {
    return (
      <div
        className={cn(
          "flex flex-wrap items-center justify-between gap-4 border-b border-[var(--color-border)] px-[var(--panel-header-pad-x)] pb-[var(--panel-header-pad-y-bottom)] pt-[var(--panel-header-pad-y-top)]",
          className,
        )}
        {...props}
      >
        {/* A panel is a top-level region of its page, so its title is an h2 and
            the outline runs h1 → h2 without a skipped level. Nested content
            inside the body takes h3. */}
        <div className="min-w-0">
          {title ? <h2 className="type-panel-title text-[var(--color-text)]">{title}</h2> : null}
          {meta ? (
            <div className="type-meta mt-(--space-1) text-[var(--color-text-secondary)]">
              {meta}
            </div>
          ) : null}
          {children}
        </div>
        {action ? <div className="ml-auto min-w-0 max-w-full shrink-0">{action}</div> : null}
      </div>
    );
  }
  return (
    <div
      className={cn(
        "border-b border-[var(--color-border)] px-[var(--panel-header-pad-x)] pb-[var(--panel-header-pad-y-bottom)] pt-[var(--panel-header-pad-y-top)]",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function PanelBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("px-[var(--layout-card-pad-x)] py-[var(--layout-card-pad-y)]", className)}
      {...props}
    />
  );
}
